import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { Component, ElementRef, Input, OnInit, ViewChild } from '@angular/core';
import {
  UntypedFormArray,
  UntypedFormBuilder,
  UntypedFormControl,
  Validators,
} from '@angular/forms';
import {
  MatLegacyAutocomplete as MatAutocomplete,
  MatLegacyAutocompleteSelectedEvent as MatAutocompleteSelectedEvent,
} from '@angular/material/legacy-autocomplete';
import { Router } from '@angular/router';
import { Address } from '@classes/address';
import { Client } from '@classes/client';
import { DictionaryValue } from '@classes/dictionary/dictionaryValue';
import { RecurringOrder } from '@classes/recurring-order/recurring-order';
import { Site } from '@classes/site';
import { ConfirmValidMatcher } from '@classes/validations/confirm-valid-matcher';
import { FormEditBaseComponent } from '@components/form-edit-base.component';
import { RoleName } from '@enums/role-name.enum';
import { AddressesService } from '@services/addresses.service';
import { AuthService } from '@services/auth.service';
import { ClientsService } from '@services/clients.service';
import { DictionaryValuesService } from '@services/dictionaryValues.service';
import { RecurringOrdersService } from '@services/recurring-orders.service';
import { SitesService } from '@services/sites.service';
import { UiService } from '@services/ui.service';
import { UsersService } from '@services/users.service';
import { Observable } from 'rxjs';
import * as moment from 'moment';
import { User } from '@classes/user/user';
import { map, startWith } from 'rxjs/operators';
import { Day } from '@enums/days.enum';
import { OrderType } from '@enums/order-type.enum';

@Component({
  selector: 'app-recurring-order-edit',
  templateUrl: './recurring-order-edit.component.html',
  styleUrls: ['./recurring-order-edit.component.scss'],
})
export class RecurringOrderEditComponent
  extends FormEditBaseComponent
  implements OnInit
{
  @Input() order: RecurringOrder;

  clients: Client[];
  sites: Site[];
  filteredSites: Site[] = [];
  selectedClient: Client;
  transportTypes: DictionaryValue[];
  urgencyLevels: DictionaryValue[];
  senders: Address[];
  receivers: Address[];
  drivers: User[];

  stringMaxLength = 50;
  commentMaxLength = 500;
  codeMaxLength = 20;
  isRestricted: boolean;
  isClient: boolean;
  mustCheckDate: boolean;
  confirmValidMatcher = new ConfirmValidMatcher();

  invalidFields: string[] = [];
  invalidMessage: string = '';

  isFtso: boolean;
  canAddOtherAddress = true;

  transportTypesSelected: DictionaryValue[] = [];
  devices: DictionaryValue[];
  filteredTransportTypes: Observable<DictionaryValue[]>;
  transportTypesCtrl = new UntypedFormControl();
  selectable = true;
  removable = true;
  addOnBlur = true;
  onEditFrequencyMode = false;
  separatorKeysCodes: number[] = [ENTER, COMMA];

  addAfterSteps: { value: number; display: string }[] = [];

  @ViewChild('deviceInput') deviceInput: ElementRef<HTMLInputElement>;
  @ViewChild('auto') matAutocomplete: MatAutocomplete;
  allTransportTypes: any;

  recurrencies = [
    {
      name: 'Par jour',
      value: 'daily',
    },
    // {
    //   name: 'Par semaine',
    //   value: 'weekly'
    // },
    {
      name: 'Par mois',
      value: 'monthly',
    },
  ];

  daily = [
    {
      name: 'Tous les jours',
      value: 'everyday',
    },
    {
      name: 'Tous les jours de semaine',
      value: 'everyWeekDays',
    },
    {
      name: 'Tous les ...',
      value: 'custom',
    },
  ];

  monthly = [
    {
      name: 'Le',
      value: 'day',
    },
    {
      name: 'Le',
      value: 'the',
    },
  ];

  nthDays = [
    {
      name: 'premier',
      value: 'first',
    },
    {
      name: 'second',
      value: 'second',
    },
    {
      name: 'troisième',
      value: 'third',
    },
  ];

  days = [
    {
      value: 1,
      checked: false,
      disabled: false,
    },
    {
      value: 2,
      checked: false,
      disabled: false,
    },
    {
      value: 3,
      checked: false,
      disabled: false,
    },
    {
      value: 4,
      checked: false,
      disabled: false,
    },
    {
      value: 5,
      checked: false,
      disabled: false,
    },
    {
      value: 6,
      checked: false,
      disabled: false,
    },
    {
      value: 0,
      checked: false,
      disabled: false,
    },
  ];

  daysSelected = [];
  daysWeeklySelected = [];
  isPonctualOrder = false;

  constructor(
    protected uiSrv: UiService,
    protected fb: UntypedFormBuilder,
    private usersSrv: UsersService,
    private authSrv: AuthService,
    private clientsSrv: ClientsService,
    private ordersSrv: RecurringOrdersService,
    private sitesSrv: SitesService,
    private dictionarySrv: DictionaryValuesService,
    private addressesSrv: AddressesService,
    private router: Router,
  ) {
    super(uiSrv, fb);
  }

  ngOnInit(): void {
    super.ngOnInit();
    // Déterminer si c'est une commande ponctuelle
    this.isPonctualOrder = !this.order.cronExpression;
    
    // tslint:disable-next-line: max-line-length
    this.isRestricted =
      this.authSrv.getUser().role.name !== RoleName.super_admin &&
      this.authSrv.getUser().role.name !== RoleName.bizzdev;
    this.isFtso =
      this.authSrv.getUser().role.name === RoleName.super_admin ||
      this.authSrv.getUser().role.name === RoleName.bizzdev ||
      this.authSrv.getUser().role.name === RoleName.admin ||
      this.authSrv.getUser().role.name === RoleName.admin_subcontractor ||
      this.authSrv.getUser().role.name === RoleName.dispatcher;
    this.isClient =
      this.authSrv.getUser().role.name === RoleName.client ||
      this.authSrv.getUser().role.name === RoleName.admin_client;

    this.initSubscriptions();
  }

  isControlSet(control: string): boolean {
    return !!this.editForm.get(control);
  }

  onSubmit(): void {
    this.isLoading = true;
    this.update();
  }

  private update(): void {
    const formValues = this.editForm.value;

    const orderCopy: RecurringOrder = JSON.parse(JSON.stringify(this.order));

    if (typeof formValues.sender[0].address === 'string') {
      formValues.sender[0].otherAddress = formValues.sender[0].address;
      formValues.sender[0].address = null;
    } else {
      formValues.sender[0].otherAddress = null;
    }

    if (this.isTour()) {
      formValues.sender[0].isDelivery = true;
      formValues.sender[0].isPickup = true;
    }

    formValues.receivers.map((receiver) => {
      if (typeof receiver.address === 'string') {
        receiver.otherAddress = receiver.address;
        receiver.address = null;
      } else {
        receiver.otherAddress = null;
      }

      if (this.isTour()) {
        receiver.isDelivery = true;
        receiver.isPickup = true;
      } else {
        if (receiver.type === 'isDelivery') {
          receiver.isDelivery = true;
          receiver.isPickup = false;
        } else {
          receiver.isDelivery = false;
          receiver.isPickup = true;
        }
      }
    });

    if (this.onEditFrequencyMode) {
      if (!this.isPonctualOrder) {
        orderCopy.cronExpression = this.createCronExpression();
        orderCopy.minutesToAdd = this.minutesToAdd();
      } else {
        // Mode ponctuel activé
        orderCopy.cronExpression = null;
        orderCopy.minutesToAdd = 0;
      }
    } else {
      if (!this.isPonctualOrder) {
        orderCopy.cronExpression = this.order.cronExpression;
        orderCopy.minutesToAdd = this.order.minutesToAdd;
      } else {
        orderCopy.cronExpression = null;
        orderCopy.minutesToAdd = 0;
      }
    }
    
    // Gérer les dates pour les commandes ponctuelles
    if (this.isPonctualOrder) {
      if (formValues.scheduledDate && formValues.scheduledTime) {
        const scheduledDateTime = new Date(formValues.scheduledDate);
        const [hours, minutes] = formValues.scheduledTime.split(':');
        scheduledDateTime.setHours(Number.parseInt(hours, 10), Number.parseInt(minutes, 10), 0);
        orderCopy.scheduledDate = scheduledDateTime;
      }
      
      if (formValues.orderStartDate && formValues.orderStartTime) {
        const orderStartDateTime = new Date(formValues.orderStartDate);
        const [hours, minutes] = formValues.orderStartTime.split(':');
        orderStartDateTime.setHours(Number.parseInt(hours, 10), Number.parseInt(minutes, 10), 0);
        orderCopy.orderStartDate = orderStartDateTime;
      }
    } else {
      // Pour les commandes récurrentes, s'assurer que les dates sont null
      orderCopy.scheduledDate = null;
      orderCopy.orderStartDate = null;
    }

    orderCopy.site = formValues.site;
    orderCopy.client = formValues.client;
    orderCopy.urgency = formValues.urgencyLevel;

    orderCopy.ordererPhone = formValues.ordererPhone;
    orderCopy.ordererComment = formValues.ordererComment;
    orderCopy.ordererName = formValues.ordererName;
    orderCopy.sender = formValues.sender[0];

    if (orderCopy.sender.requestedTime) {
      orderCopy.sender.requestedTime = this.formatTimeWithSeconds(
        orderCopy.sender.requestedTime,
      );
    }

    orderCopy.receivers = formValues.receivers;
    orderCopy.receivers.forEach((receiver) => {
      if (receiver.requestedTime) {
        receiver.requestedTime = this.formatTimeWithSeconds(
          receiver.requestedTime,
        );
      }
    });
    orderCopy.transportTypes = this.transportTypesSelected;

    orderCopy.name = formValues.name;

    if (this.isTour()) {
      orderCopy.price = formValues.price;
      orderCopy.extraStepPrice = formValues.extraStepPrice;
      orderCopy.driver = formValues.driver || null;
      orderCopy.code = formValues.code;
    }

    orderCopy.isActiveOnHolidays = formValues.isActiveOnHolidays;

    this.ordersSrv.put(orderCopy).subscribe(() => {
      this.isLoading = false;
      this.onFinish();
      this.uiSrv.showSnackbar('orders.edit.edited', true);
    });
  }

  private initSubscriptions(): void {
    this.isLoading = true;

    const driversPromise = new Promise((resolve, reject) => {
      this.usersSrv.getList(null, true).subscribe((usersFromSrv) => {
        this.drivers = usersFromSrv.filter(
          (user) =>
            user.role.name !== RoleName.admin_client &&
            user.role.name !== RoleName.client,
        );
        resolve(true);
      });
    });

    const sitesPromise = new Promise((resolve, reject) => {
      // Charger les sites filtrés par l'agence de la commande
      if (this.order.site.clients && this.order.site.clients.length > 0) {
        const clientIds = this.order.site.clients.map(c => c.id);
        this.sitesSrv.getNonTransmissionOnlyListByClients(clientIds).subscribe((sitesFromSrv) => {
          this.filteredSites = sitesFromSrv;
          resolve(true);
        });
      } else if (this.order.client) {
        this.sitesSrv.getNonTransmissionOnlyListByClients([this.order.client.id]).subscribe((sitesFromSrv) => {
          this.filteredSites = sitesFromSrv;
          resolve(true);
        });
      } else {
        this.sitesSrv.getNonTransmissionOnlyList().subscribe((sitesFromSrv) => {
          this.sites = sitesFromSrv;
          this.filteredSites = sitesFromSrv;
          resolve(true);
        });
      }
    });

    const clientsPromise = new Promise((resolve, reject) => {
      this.clientsSrv.getList().subscribe((clientsFromSrv) => {
        this.clients = clientsFromSrv;
        resolve(true);
      });
    });

    const typeTransportsPromise = new Promise((resolve, reject) => {
      this.dictionarySrv
        .getTransportListBySite(this.order.site, this.order.client?.id)
        .subscribe((transportsFromSrv) => {
          this.allTransportTypes = transportsFromSrv;
          resolve(true);
        });
    });

    const urgencyLevelsPromise = new Promise((resolve, reject) => {
      this.dictionarySrv
        .getUrgencyListBySite(this.order.site, this.order.client?.id)
        .subscribe((urgencyLevelsFromSrv) => {
          this.urgencyLevels = urgencyLevelsFromSrv;
          this.urgencyLevels.map((urgency) => {
            if (this.order.urgency.id === urgency.id) {
              urgency.isChecked = true;
            }
          });
          resolve(true);
        });
    });

    const addressPromise = new Promise((resolve) => {
      this.addressesSrv
        .getListForSite(this.order.site.id)
        .subscribe((addressessFromSrv) => {
          this.senders = addressessFromSrv.filter(
            (address) => address.isSender,
          );

          this.receivers = addressessFromSrv.filter(
            (address) => address.isReceiver,
          );

          resolve(true);
        });
    });

    Promise.all([
      sitesPromise,
      clientsPromise,
      typeTransportsPromise,
      urgencyLevelsPromise,
      addressPromise,
      driversPromise,
    ]).then(() => {
      this.isLoading = false;
      this.initForm();
    });
  }

  private initForm(): void {
    const fullSite = this.filteredSites?.find((site) => site.id === this.order.site?.id);
    this.canAddOtherAddress = fullSite?.allowOtherAddress !== false;

    // Extraire les dates et heures pour les commandes ponctuelles
    let scheduledDate = null;
    let scheduledTime = null;
    let orderStartDate = null;
    let orderStartTime = null;
    
    if (this.isPonctualOrder && this.order.scheduledDate) {
      const scheduled = new Date(this.order.scheduledDate);
      scheduledDate = scheduled.toISOString().split('T')[0];
      scheduledTime = scheduled.toTimeString().substring(0, 5);
    }
    
    if (this.isPonctualOrder && this.order.orderStartDate) {
      const orderStart = new Date(this.order.orderStartDate);
      orderStartDate = orderStart.toISOString().split('T')[0];
      orderStartTime = orderStart.toTimeString().substring(0, 5);
    }
    
    // Déterminer le client initial
    let initialClient = this.order.client;
    if (!initialClient && this.order.site.clients && this.order.site.clients.length > 0) {
      initialClient = this.order.site.clients[0];
    }
    this.selectedClient = initialClient!;
    
    this.editForm = this.fb.group({
      client: new UntypedFormControl(initialClient, Validators.required),
      ordererComment: [
        this.order.ordererComment,
        [Validators.maxLength(this.commentMaxLength)],
      ],
      // Champs pour commande ponctuelle
      isPonctual: new UntypedFormControl(this.isPonctualOrder),
      scheduledDate: new UntypedFormControl(scheduledDate),
      scheduledTime: new UntypedFormControl(scheduledTime),
      orderStartDate: new UntypedFormControl(orderStartDate),
      orderStartTime: new UntypedFormControl(orderStartTime),
      // Champs pour commande récurrente
      creationTime: new UntypedFormControl(null),
      startTime: new UntypedFormControl(null),
      name: new UntypedFormControl(this.order.name, [Validators.maxLength(50)]),
      recurrency: new UntypedFormControl(null),
      dailyRecurrency: new UntypedFormControl(null),
      weeklyRecurrency: new UntypedFormControl(null),
      dayMonthlyRecurrency: new UntypedFormControl(null),
      urgencyLevel: new UntypedFormControl(null, [Validators.required]),
      schedule: this.fb.array([]),
      sender: this.fb.array([], Validators.required),
      receivers: this.fb.array([], Validators.required),
      ordererPhone: new UntypedFormControl(this.order.ordererPhone, [
        Validators.maxLength(this.stringMaxLength),
      ]),
      ordererName: new UntypedFormControl(this.order.ordererName, [
        Validators.maxLength(this.stringMaxLength),
      ]),
      site: new UntypedFormControl(this.order.site, [Validators.required]),
      isActiveOnHolidays: new UntypedFormControl(this.order.isActiveOnHolidays),
      addAfterStep: new UntypedFormControl(null),
    });
    
    // Gestion du toggle commande ponctuelle
    this.editForm.get('isPonctual').valueChanges.subscribe((isPonctual) => {
      this.isPonctualOrder = isPonctual;
      this.updateFormValidators();
    });
    
    this.updateFormValidators();

    if (this.isTour()) {
      this.editForm.addControl('price', new UntypedFormControl());
      this.editForm.addControl('extraStepPrice', new UntypedFormControl());
      this.editForm.addControl('driver', new UntypedFormControl());
      this.editForm.addControl(
        'code',
        new UntypedFormControl(this.order.code, [
          Validators.maxLength(this.codeMaxLength),
        ]),
      );

      this.getControl('price').setValue(this.order.price);
      this.getControl('extraStepPrice').setValue(this.order.extraStepPrice);
      this.getControl('driver').setValue(this.order.driver);
    }

    // Pré-sélectionner le site actuel dans la liste filtrée
    const currentSite = this.filteredSites.find(s => s.id === this.order.site.id);
    if (currentSite) {
      this.editForm.get('site')!.setValue(currentSite);
    }

    // Observer les changements de client pour filtrer les sites
    this.editForm.get('client')!.valueChanges.subscribe(client => {
      if (client && typeof client !== 'string') {
        this.selectedClient = client;
        
        // Filtrer les sites par client sélectionné
        this.sitesSrv.getNonTransmissionOnlyListByClients([client.id]).subscribe((sitesFromSrv) => {
          this.filteredSites = sitesFromSrv;
          
          // Réinitialiser le site sélectionné si ce n'est pas dans la nouvelle liste
          const currentSite = this.editForm.get('site')!.value;
          if (currentSite && !this.filteredSites.find(s => s.id === currentSite.id)) {
            this.editForm.get('site')!.setValue(null);
            // Réinitialiser aussi les adresses, urgences et transports
            this.senders = [];
            this.receivers = [];
            this.urgencyLevels = [];
            this.allTransportTypes = [];
            this.transportTypesSelected = [];
            this.editForm.get('urgencyLevel')!.setValue(null);
          } else if (currentSite) {
            // Le site actuel est toujours valide, recharger ses données avec le nouveau client
            this.dictionarySrv
              .getTransportListBySite(currentSite, client.id)
              .subscribe((transportsFromSrv) => {
                this.allTransportTypes = transportsFromSrv;
                // Filtrer les types de transport sélectionnés qui ne sont plus disponibles
                this.transportTypesSelected = this.transportTypesSelected.filter(selected => 
                  this.allTransportTypes.some((available: DictionaryValue) => available.id === selected.id)
                );
                // Forcer la mise à jour de la liste filtrée
                this.transportTypesCtrl.setValue(null);
                this.checkIfSelected();
              });
            this.dictionarySrv
              .getUrgencyListBySite(currentSite, client.id)
              .subscribe((urgenciesFromSrv) => {
                this.urgencyLevels = urgenciesFromSrv;
                // Vérifier si le niveau d'urgence actuel est toujours valide
                const currentUrgency = this.editForm.get('urgencyLevel')!.value;
                if (currentUrgency && !this.urgencyLevels.find(u => u.id === currentUrgency.id)) {
                  this.editForm.get('urgencyLevel')!.setValue(null);
                }
              });
          }
        });
      } else {
        // Client supprimé - réinitialiser
        this.selectedClient = null!;
        this.filteredSites = [];
        this.editForm.get('site')!.setValue(null);
      }
    });

    this.editForm.get('site')!.valueChanges.subscribe((site) => {
      this.canAddOtherAddress = !site || typeof site === 'string' || site.allowOtherAddress !== false;

      if (site && site.id) {
        const selectedClient = this.editForm.get('client')!.value;
        
        // Recharger les adresses pour le nouveau site
        this.addressesSrv
          .getListForSite(site.id)
          .subscribe((addressessFromSrv) => {
            this.senders = addressessFromSrv.filter(
              (address) => address.isSender,
            );
            this.receivers = addressessFromSrv.filter(
              (address) => address.isReceiver,
            );

            this.getArray('sender').controls.map((control) => {
              control.get('address')!.setValue(null);
            });
            this.getArray('receivers').controls.map((control) => {
              control.get('address')!.setValue(null);
            });
          });
        
        // Recharger les types de transport pour le nouveau site
        this.dictionarySrv
          .getTransportListBySite(site, selectedClient?.id)
          .subscribe((transportsFromSrv) => {
            this.allTransportTypes = transportsFromSrv;
            // Réinitialiser les types de transport sélectionnés
            this.transportTypesSelected = [];
            // Forcer la mise à jour de la liste filtrée
            this.transportTypesCtrl.setValue(null);
            this.checkIfSelected();
          });
        
        // Recharger les niveaux d'urgence pour le nouveau site
        this.dictionarySrv
          .getUrgencyListBySite(site, selectedClient?.id)
          .subscribe((urgenciesFromSrv) => {
            this.urgencyLevels = urgenciesFromSrv;
            // Réinitialiser le niveau d'urgence sélectionné
            this.editForm.get('urgencyLevel')!.setValue(null);
          });
      }
    });

    this.urgencyLevels.map((urgency) => {
      if (urgency.isChecked) {
        this.editForm.get('urgencyLevel')!.setValue(urgency);
      }
    });

    // Initialiser filteredTransportTypes seulement si pas encore fait
    if (!this.filteredTransportTypes) {
      this.filteredTransportTypes = this.transportTypesCtrl.valueChanges.pipe(
        startWith(null),
        map((fruit: string | null) =>
          fruit ? this._filter(fruit) : this.allTransportTypes.slice(),
        ),
      );
    }

    if (this.order.transportTypes) {
      this.order.transportTypes.map((transportType) => {
        this.transportTypesSelected.push(transportType);
      });

      this.checkIfSelected();
    }

    this.onAddSender(this.order.sender);

    if (this.order.receivers) {
      this.order.receivers.map((receiver, index) => {
        this.onAddReceiver(receiver, index);
      });
    } else {
      this.onAddReceiver();
    }

    // this.initDateForm();

    this.editForm.valueChanges.subscribe(() => {
      this.invalidFields = this.findInvalidControls();
      this.invalidMessage = 'Le formulaire est incomplet';
      this.invalidFields.map((field) => {
        this.invalidMessage = this.invalidMessage + ' ' + field;
      });
    });
  }

  public findInvalidControls() {
    const invalid = [];
    const controls = this.editForm.controls;
    for (const name in controls) {
      if (controls[name].invalid) {
        invalid.push(this.translateFields(name));
      }
    }

    //check si le dernier receiver est bien une livraison
    if (!this.isTour()) {
      let lastReceiver =
        this.editForm.value.receivers[this.editForm.value.receivers.length - 1];

      if (lastReceiver.type === 'isPickup') {
        invalid.push(' | La dernière étape est un enlèvement');
      }
    }

    if (this.transportTypesSelected.length === 0) {
      invalid.push(" | Il n'y a aucun type de produit");
    }

    return invalid;
  }

  translateFields(name): string {
    if (name === 'site') {
      return " | Donneur d'ordre";
    }
    if (name === 'urgencyLevel') {
      return " | Niveau d'urgence";
    }
    if (name === 'sender') {
      return this.isTour()
        ? '| étape 1 incomplète'
        : " | Lieu d'enlèvement incomplet";
    }
    if (name === 'receivers') {
      return this.isTour()
        ? '| étape(s) incomplète(s)'
        : ' | Destinataire(s) incomplet(s)';
    }
  }

  minTransportType(min = 1): any {
    return (formArray: UntypedFormArray) => {
      return formArray.controls.length >= min ? null : { required: true };
    };
  }

  // onAddTransportType(transportType?: DictionaryValue): void {

  //   this.getArray('transportTypes').push(this.fb.control(transportType));
  // }

  removeTransportType(transportTypeToRemove): void {
    this.getArray('transportTypes').controls.splice(
      this.getArray('transportTypes').controls.indexOf(transportTypeToRemove),
      1,
    );
    this.editForm.value.transportTypes.splice(
      this.editForm.value.transportTypes.indexOf(transportTypeToRemove.value),
      1,
    );
  }

  get transportTypesFields(): UntypedFormArray {
    return this.editForm.get('transportTypes') as UntypedFormArray;
  }

  // private scheduleToDate(schedule: any): Date {
  //   if (!schedule.startDate || !schedule.startTime || typeof schedule.startDate === 'string') {
  //     return null;
  //   }
  //
  //   const startDate: moment.Moment = schedule.startDate.local();
  //   const startTime: number[] = schedule.startTime.split(':').map(s => parseInt(s, 10));
  //   return startDate.hours(startTime[0]).minutes(startTime[1]).utc().toDate();
  // }

  // initDateForm(): void {
  //   const startMoment: moment.Moment = moment(new Date(this.order.startDate));
  //   const startTime: string = moment(startMoment).format('HH:mm');
  //   const newGroup = this.fb.group({
  //     startDate: [startMoment, Validators.required],
  //     startTime: [startTime, Validators.required]
  //   });

  //   this.getArray('schedule').push(newGroup);

  //   newGroup.controls.startDate.valueChanges
  //     .subscribe(() => {
  //       this.mustCheckDate = true;
  //     });

  //   newGroup.controls.startTime.valueChanges
  //     .subscribe(() => {
  //       this.mustCheckDate = true;
  //     });
  // }

  resetStartDate(control) {
    control.get('startDate').reset();
  }

  resetBirthDate(control) {
    control.get('birthdate').reset();
  }

  updateAddAfterSteps() {
    this.addAfterSteps = [
      {
        value: 1,
        display: "lieu d'enlèvement",
      },
    ];
    const receiversLength = this.getArray('receivers').length;
    for (let i = 0; i < receiversLength; i++) {
      this.addAfterSteps.push({
        value: i + 2,
        display: 'étape ' + (i + 2),
      });
    }
  }

  onAddReceiver(receiver?, index?): void {
    let address = null;
    let otherAddress = null;
    let service = null;
    let ordererComment = null;
    let type = null;
    let id = null;
    let requestedTime = null;

    if (receiver) {
      id = receiver.id;
      address = receiver.address;
      otherAddress = receiver.otherAddress;
      service = receiver.service;
      ordererComment = receiver.ordererComment;
      requestedTime = receiver.requestedTime;
      if (receiver.isPickup) {
        type = 'isPickup';
      } else {
        type = 'isDelivery';
      }
    }

    if (this.isTour()) {
      type = 'isDelivery';
    }

    const newGroup = this.fb.group({
      id: [id],
      address: [address, [Validators.required]],
      otherAddress: [otherAddress],
      service: [service, [Validators.required]],
      ordererComment: [
        ordererComment,
        [Validators.maxLength(this.commentMaxLength)],
      ],
      type: [type, [Validators.required]],
      requestedTime: [requestedTime],
    });

    if (receiver) {
      if (receiver.otherAddress) {
        newGroup.get('address').setValue(receiver.otherAddress);
        newGroup.get('otherAddress').setValue(receiver.otherAddress);
      } else {
        this.receivers.map((oneReceiver) => {
          if (oneReceiver.id === receiver.address.id) {
            newGroup.get('address').setValue(oneReceiver);
          }
        });
      }
    }
    if (this.editForm.get('addAfterStep').value) {
      this.getArray('receivers').insert(
        this.editForm.get('addAfterStep').value - 1,
        newGroup,
      );
      this.editForm.get('addAfterStep').setValue(null);
    } else {
      this.getArray('receivers').push(newGroup);
    }
    this.updateAddAfterSteps();
  }



  private addZero(num: number): string {
    return num < 10 ? `0${num}` : `${num}`;
  }

  private formatTimeWithSeconds(time: string): string {
    if (!time) {
      return null;
    }
    // Si le format est déjà HH:MM:SS, on le retourne tel quel
    if (time.split(':').length === 3) {
      return time;
    }
    // Sinon, on ajoute :00 pour les secondes
    return time + ':00';
  }

  onRemoveReceiver(index: number): void {
    this.getArray('receivers').removeAt(index);
    this.updateAddAfterSteps();
  }

  onAddSender(sender?): void {
    let address = null;
    let otherAddress = null;
    let service = null;
    let ordererComment = null;
    let type = null;
    let id = null;
    let requestedTime = null;

    if (sender) {
      id = sender.id;
      address = sender.address;
      otherAddress = sender.otherAddress;
      service = sender.service;
      ordererComment = sender.ordererComment;
      requestedTime = sender.requestedTime;
    }

    const newGroup = this.fb.group({
      id: [id],
      address: [address, [Validators.required]],
      otherAddress: [otherAddress],
      service: [service, [Validators.required]],
      ordererComment: [
        ordererComment,
        [Validators.maxLength(this.commentMaxLength)],
      ],
      requestedTime: [requestedTime],
      isPickup: true,
      isDelivery: false,
    });

    if (sender) {
      if (sender.otherAddress) {
        newGroup.get('address').setValue(sender.otherAddress);
        newGroup.get('otherAddress').setValue(sender.otherAddress);
      } else {
        this.senders.map((oneSender) => {
          if (oneSender.id === sender.address.id) {
            newGroup.get('address').setValue(oneSender);
          }
        });
      }
    }
    this.getArray('sender').push(newGroup);
  }

  onCheckboxChange(e): any {
    const checkArray: UntypedFormArray = this.editForm.get(
      'transportTypes',
    ) as UntypedFormArray;

    if (e.checked) {
      checkArray.push(new UntypedFormControl(e.source.value));
    } else {
      let i: number = 0;
      checkArray.controls.forEach((item: UntypedFormControl) => {
        if (item.value === e.source.value) {
          checkArray.removeAt(i);
          return;
        }
        i++;
      });
    }
  }

  onFinish(): void {
    this.editFinished.emit();
  }

  // checkWhatChanged(orderCopy): string[] {
  //   let modifiedFields = [];

  //   if (this.order.site.id !== orderCopy.site.id) {
  //     modifiedFields.push('Donneur d\'ordre');
  //   }

  //   if (this.order.transportTypes.length !== orderCopy.transportTypes.length) {
  //     modifiedFields.push('Type de produit')
  //   } else {
  //     let hasChanged = false;
  //     orderCopy.transportTypes.map((transportType, index) => {
  //       if (this.order.transportTypes[index].id !== transportType.id) {
  //         hasChanged = true;
  //       }
  //     })

  //     if (hasChanged) {
  //       modifiedFields.push('Type de produit');
  //     }
  //   }

  //   if (this.order.urgency.id !== orderCopy.urgency.id) {
  //     modifiedFields.push('Niveau d\'urgence');
  //   }

  //   if (new Date(this.order.startDate).toString() !== new Date(orderCopy.startDate).toString()) {
  //     modifiedFields.push('Dates/heures d\'enlèvement');
  //   }

  //   if (this.order.sender.address) {
  //     if (orderCopy.sender.address) {
  //       if (this.order.sender.address.id !== orderCopy.sender.address.id) {
  //         modifiedFields.push('Adresse de l\'étape 1');
  //       }
  //     } else {
  //       modifiedFields.push('Adresse de l\'étape 1');
  //     }
  //   } else if (this.order.sender.otherAddress) {
  //     if (orderCopy.sender.otherAddress) {
  //       if (this.order.sender.otherAddress !== orderCopy.sender.otherAddress) {
  //         modifiedFields.push('Adresse de l\'étape 1');
  //       }
  //     } else {
  //       modifiedFields.push('Adresse de l\'étape 1');
  //     }
  //   }
  //   if (this.order.sender.service !== orderCopy.sender.service) {
  //     modifiedFields.push('Sevice de l\'étape 1')
  //   }

  //   if (this.order.sender.ordererComment !== orderCopy.sender.ordererComment) {
  //     modifiedFields.push('Commentaire de l\'étape 1');
  //   }

  //   if (this.order.ordererName !== orderCopy.ordererName) {
  //     modifiedFields.push('Nom du demandeur');
  //   }

  //   if (this.order.ordererPhone !== orderCopy.ordererPhone) {
  //     modifiedFields.push('Téléphone du demandeur');
  //   }

  //   if (this.order.receivers.length !== orderCopy.receivers.length) {
  //     modifiedFields.push('Étape(s)');
  //   } else {
  //     let hasChanged = false;
  //     orderCopy.receivers.map(receiver => {
  //       this.order.receivers.map((oldReceiver, index) => {
  //         let position = index + 2;
  //         if (receiver.address) {
  //           if (oldReceiver.address) {
  //             if (receiver.address.id !== oldReceiver.address.id) {
  //               modifiedFields.push('Adresse de l\'étape ' + position);
  //             }

  //           } else {
  //             modifiedFields.push('Adresse de l\'étape ' + position);
  //           }
  //           if (receiver.service !== oldReceiver.service) {
  //             modifiedFields.push('Service de l\'étape ' + position);
  //           }
  //           if (receiver.ordererComment !== oldReceiver.ordererComment) {
  //             modifiedFields.push('Commentaire de l\'étape ' + position);
  //           }
  //         } else if (receiver.otherAddress) {
  //           if (oldReceiver.otherAddress) {
  //             if (receiver.otherAddress !== oldReceiver.otherAddress) {
  //               modifiedFields.push('Adresse de l\'étape ' + position);
  //             }

  //           } else {
  //             modifiedFields.push('Adresse de l\'étape ' + position);
  //           }
  //           if (receiver.service !== oldReceiver.service) {
  //             modifiedFields.push('Service de l\'étape ' + position);
  //           }
  //           if (receiver.ordererComment !== oldReceiver.ordererComment) {
  //             modifiedFields.push('Commentaire de l\'étape ' + position);
  //           }
  //         }
  //       })
  //     })
  //   }

  //   if (this.order.patients.length !== orderCopy.patients.length) {
  //     modifiedFields.push('Liste patients');
  //   } else {
  //     orderCopy.patients.map((patient, index) => {
  //       let position = index + 1;
  //       if (patient.firstname != this.order.patients[index].firstname || patient.birthname != this.order.patients[index].birthname || patient.lastname != this.order.patients[index].lastname || new Date(patient.birthdate).toString() != new Date(this.order.patients[index].birthdate).toString() || patient.productType != this.order.patients[index].productType || patient.ipp != this.order.patients[index].ipp) {
  //         modifiedFields.push('Patient ' + position);
  //       }
  //     })
  //   }

  //   for (let i = 1; i < modifiedFields.length; i++) {
  //     modifiedFields[i] = " " + modifiedFields[i];
  //   }

  //   return modifiedFields;
  // }

  remove(fruit: DictionaryValue): void {
    const index = this.transportTypesSelected.indexOf(fruit);

    if (index >= 0) {
      this.transportTypesSelected.splice(index, 1);
    }

    this.checkIfSelected();
  }

  selected(event: MatAutocompleteSelectedEvent): void {
    document.getElementById('deviceChipList').blur();
    if (!this.transportTypesSelected.includes(event.option.value)) {
      this.transportTypesSelected.push(event.option.value);
      // this.devices = this._filter(event.option.value);
      this.checkIfSelected();

      this.deviceInput.nativeElement.value = '';
    }
  }

  checkIfSelected() {
    this.filteredTransportTypes = this.filteredTransportTypes.pipe(
      map((array) => {
        return array.map((element) => {
          element.selected = false;
          this.transportTypesSelected.map((selected) => {
            if (selected.id === element.id) {
              element.selected = true;
            }
          });
          return element;
        });
      }),
    );
  }

  private _filter(value?): DictionaryValue[] {
    let filterValue = '';

    if (value.name) {
      filterValue = value.name.toLowerCase();
      return this.allTransportTypes.filter((el) => {
        return el.id === value.id;
      });
    } else {
      filterValue = value.toLowerCase();
      return this.allTransportTypes.filter(
        (transportType) =>
          transportType.name.toLowerCase().indexOf(filterValue) === 0,
      );
    }
  }

  clearDevicesSelected(): void {
    this.transportTypesSelected = [];
    this.filteredTransportTypes.pipe(
      map((fruit) => {
        return this.allTransportTypes;
      }),
    );
    this.checkIfSelected();
  }

  editFrequency() {
    this.onEditFrequencyMode = true;
    this.updateFormValidators();
  }

  cancelNewFrequency() {
    this.daysSelected = [];
    this.onEditFrequencyMode = false;
    this.updateFormValidators();
  }

  selectDay(event, day) {
    if (event.source.checked === true) {
      day.checked = true;
      this.daysSelected.push(day.value);
    } else {
      day.checked = false;
      this.daysSelected.map((oneDay, index) => {
        if (oneDay === day.value) {
          this.daysSelected.splice(index, 1);
        }
      });
    }
  }

  selectWeeklyDay(event, day) {
    if (event.source.checked === true) {
      day.checked = true;
      this.daysWeeklySelected.push(day.value);
    } else {
      day.checked = false;
      this.daysWeeklySelected.map((oneDay, index) => {
        if (oneDay === day.value) {
          this.daysWeeklySelected.splice(index, 1);
        }
      });
    }
  }

  showDayText(day: Day): string {
    return 'billing.schedules.days.' + Day[day];
  }

  createCronExpression(): string {
    let cronExpression = '';
    let formValues = this.editForm.value;
    let minutes = formValues.creationTime.split(':')[1];
    let hours = formValues.creationTime.split(':')[0];
    cronExpression += minutes + ' ' + hours;

    if (formValues.recurrency === 'daily') {
      if (formValues.dailyRecurrency === 'everyday') {
        cronExpression += ' * * *';
      } else if (formValues.dailyRecurrency === 'everyWeekDays') {
        cronExpression += ' * * 1-5';
      } else if (formValues.dailyRecurrency === 'custom') {
        let days = '';
        this.daysSelected.map((day, index) => {
          if (index === 0) {
            days += day;
          } else {
            days += ',' + day;
          }
        });
        cronExpression += ' * * ' + days;
      }
    } else if (formValues.recurrency === 'weekly') {
      let frequency = '*/' + formValues.weeklyRecurrency;
      let days = '';
      this.daysSelected.map((day, index) => {
        if (index === 0) {
          days += day;
        } else {
          days += ',' + day;
        }
      });

      cronExpression += ' 0 * ' + days;
    } else if (formValues.recurrency === 'monthly') {
      // if (formValues.monthlyRecurrency === 'day') {
      let day = formValues.dayMonthlyRecurrency;
      // let month = formValues.nthMonthMonthlyRecurrency;

      cronExpression += ' ' + day + ' *' + ' *';
      // } else if (formValues.monthlyRecurrency ===  'the') {

      // }
    }

    return cronExpression;
  }

  minutesToAdd(): number {
    let formValues = this.editForm.value;

    let minutesCreationTime = Number.parseInt(formValues.creationTime.split(':')[1], 10);
    let hoursCreationTime = Number.parseInt(formValues.creationTime.split(':')[0], 10);
    let totalMinutesCreationTime = hoursCreationTime * 60 + minutesCreationTime;

    let minutesStartTime = Number.parseInt(formValues.startTime.split(':')[1], 10);
    let hoursStartTime = Number.parseInt(formValues.startTime.split(':')[0], 10);
    let totalMinutesStartTime = hoursStartTime * 60 + minutesStartTime;

    let minutesToAdd = totalMinutesStartTime - totalMinutesCreationTime;

    return minutesToAdd;
  }

  private updateFormValidators(): void {
    if (this.isPonctualOrder) {
      // Commande ponctuelle : dates requises, récurrence non requise
      this.editForm.get('scheduledDate').setValidators([Validators.required]);
      this.editForm.get('scheduledTime').setValidators([Validators.required]);
      this.editForm.get('orderStartDate').setValidators([Validators.required]);
      this.editForm.get('orderStartTime').setValidators([Validators.required]);
      
      this.editForm.get('creationTime').clearValidators();
      this.editForm.get('startTime').clearValidators();
      this.editForm.get('recurrency').clearValidators();
    } else {
      // Commande récurrente : récurrence requise, dates non requises
      if (this.onEditFrequencyMode) {
        this.editForm.get('creationTime').setValidators([Validators.required]);
        this.editForm.get('startTime').setValidators([Validators.required]);
      } else {
        this.editForm.get('creationTime').clearValidators();
        this.editForm.get('startTime').clearValidators();
      }
      
      this.editForm.get('scheduledDate').clearValidators();
      this.editForm.get('scheduledTime').clearValidators();
      this.editForm.get('orderStartDate').clearValidators();
      this.editForm.get('orderStartTime').clearValidators();
    }
    
    // Mettre à jour la validation
    this.editForm.get('scheduledDate').updateValueAndValidity();
    this.editForm.get('scheduledTime').updateValueAndValidity();
    this.editForm.get('orderStartDate').updateValueAndValidity();
    this.editForm.get('orderStartTime').updateValueAndValidity();
    this.editForm.get('creationTime').updateValueAndValidity();
    this.editForm.get('startTime').updateValueAndValidity();
    this.editForm.get('recurrency').updateValueAndValidity();
  }

  public isTour(): boolean {
    if (!this.order) return false;

    return this.order.type === OrderType.tour;
  }

  isTypeOf(type: string, value: any): boolean {
    return typeof value === type;
  }
}
